// ============================================================
// api.js — Cliente HTTP central para Choyero Backend
// Todas las llamadas a la API pasan por aquí
// ============================================================

const BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000/api');
if (process.env.NODE_ENV === 'production' && !BASE_URL) {
  console.warn('[API] BASE_URL está vacío en producción — REACT_APP_API_URL no configurado');
}

// ── Token storage ──
export const getToken = () => localStorage.getItem('choyero_token');
export const setToken = (t) => localStorage.setItem('choyero_token', t);
export const setRefreshToken = (t) => localStorage.setItem('choyero_refresh', t);
export const getRefreshToken = () => localStorage.getItem('choyero_refresh');
export const clearTokens = () => {
  localStorage.removeItem('choyero_token');
  localStorage.removeItem('choyero_refresh');
  localStorage.removeItem('choyero_usuario');
  localStorage.removeItem('choyero_store');
};

export const getUsuarioGuardado = () => {
  try { return JSON.parse(localStorage.getItem('choyero_usuario') || 'null'); } catch { return null; }
};
export const guardarUsuario = (u) => localStorage.setItem('choyero_usuario', JSON.stringify(u));

// ── Fetch base con auth automática ──
const _fetch = async (path, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {})
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  // 401 con token activo → intentar refresh; sin token es login fallido, dejar pasar el error
  if (res.status === 401 && token) {
    const refreshed = await _intentarRefresh();
    if (refreshed) {
      const headers2 = { ...headers, Authorization: `Bearer ${getToken()}` };
      const res2 = await fetch(`${BASE_URL}${path}`, { ...options, headers: headers2 });
      const data2 = await res2.json();
      if (!res2.ok) throw new Error(data2.error || 'Error de autenticación');
      return data2;
    }
    // Refresh fallido → sesión expirada → redirigir a login
    clearTokens();
    window.location.href = '/login';
    return;
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

// Promise compartida para evitar múltiples refreshes simultáneos (race condition)
let _refreshPromise = null;

const _doRefresh = async () => {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt })
    });
    if (!res.ok) return false;
    const { accessToken, refreshToken } = await res.json();
    setToken(accessToken);
    setRefreshToken(refreshToken);
    return true;
  } catch (err) {
    console.error('[API] Token refresh failed:', err?.message);
    return false;
  }
};

const _intentarRefresh = () => {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = _doRefresh().finally(() => { _refreshPromise = null; });
  return _refreshPromise;
};

// Exportado para que useSocket pueda refrescar el token cuando expira la conexión
export const intentarRefreshToken = _intentarRefresh;

const get = (path, params) => {
  if (params && Object.keys(params).length > 0) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    if (qs) path = `${path}${path.includes('?') ? '&' : '?'}${qs}`;
  }
  return _fetch(path, { method: 'GET' });
};
const post = (path, body) => _fetch(path, { method: 'POST', body: JSON.stringify(body) });
const patch = (path, body) => _fetch(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = (path) => _fetch(path, { method: 'DELETE' });

// Bootstrap cache — lo setea apiLogin, lo consume useApiStore una sola vez en el init
let _bootstrapPendiente = null;
export const consumeBootstrapPendiente = () => {
  const d = _bootstrapPendiente;
  _bootstrapPendiente = null;
  return d;
};

// ============================================================
// AUTH
// ============================================================
export const apiLogin = async (email, password) => {
  const data = await post('/auth/login', { email, password });
  setToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  guardarUsuario(data.usuario);
  if (data.bootstrapDatos) _bootstrapPendiente = data.bootstrapDatos;
  return data.usuario;
};

export const apiRegistrarCliente = async (nombre, email, password) => {
  const data = await post('/auth/register', { nombre, email, password });
  setToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  guardarUsuario(data.usuario);
  return data.usuario;
};

export const apiRegistrarTransportista = async (empresa, credenciales, codigoReferido, codigoPromo) => {
  const data = await post('/auth/register-transportista', {
    empresa, credenciales, terminosVersion: '2026-04',
    ...(codigoReferido ? { codigoReferido } : {}),
    ...(codigoPromo ? { codigoPromo: codigoPromo.trim().toUpperCase() } : {}),
  });
  return data;
};

export const apiRegistrarClienteCompleto = async (datos, credenciales, codigoReferido) => {
  const data = await post('/auth/register-cliente', { datos, credenciales, terminosVersion: '2026-04', ...(codigoReferido ? { codigoReferido } : {}) });
  return data;
};

export const apiGetMiCodigo = () => get('/auth/mi-codigo');

// ============================================================
// REFERIDOS
// ============================================================
export const apiGetMisReferidos = () => get('/referidos/mis-referidos');
export const apiGetArbolReferidos = () => get('/referidos/arbol').then(d => d.arbol);
export const apiGetArbolReferidosAdmin = (userId) => get(`/referidos/arbol-admin/${userId}`).then(d => d.arbol);

export const apiCheckEmail = async (email) => {
  const data = await post('/auth/check-email', { email });
  return data.disponible;
};

export const apiLogout = async () => {
  try { await post('/auth/logout', {}); } catch { /* ignorar */ }
  clearTokens();
};

export const apiMe = () => get('/auth/me').then(d => d.usuario);

// ============================================================
// SOLICITUDES
// ============================================================
export const apiGetSolicitudes = () => get('/solicitudes').then(d => d.solicitudes);
export const apiGetSolicitudesDisponibles = () => get('/solicitudes/disponibles').then(d => d.solicitudes);
export const apiGetSolicitud = (id) => get(`/solicitudes/${id}`).then(d => d.solicitud);
export const apiCrearSolicitud = (datos) => post('/solicitudes', datos).then(d => d.solicitud);
export const apiCancelarSolicitud = (id, motivo) => _fetch(`/solicitudes/${id}`, { method: 'DELETE', body: JSON.stringify({ motivo: motivo || null }) });
export const apiEditarSolicitud = (id, datos) => patch(`/solicitudes/${id}`, datos).then(d => d.solicitud);

// ============================================================
// COTIZACIONES
// ============================================================
export const apiGetCotizaciones = (solicitudId) => get(`/cotizaciones/${solicitudId}`).then(d => d.cotizaciones);
export const apiEnviarCotizacion = (datos) => post('/cotizaciones', datos).then(d => d.cotizacion);
export const apiAceptarCotizacion = (id, datos) => post(`/cotizaciones/${id}/aceptar`, datos).then(d => d.viaje);

// ============================================================
// VIAJES
// ============================================================
export const apiGetViajesActivos = () => get('/viajes/activos').then(d => d.viajes);
export const apiGetViajesEmpresa = () => get('/viajes/empresa').then(d => d.viajes);
export const apiGetViaje = (id) => get(`/viajes/${id}`).then(d => d.viaje);
export const apiAsignarChofer = (viajeId, datos) => post(`/viajes/${viajeId}/asignar-chofer`, datos).then(d => d.orden);
export const apiReasignarChofer = (viajeId, datos) => post(`/viajes/${viajeId}/reasignar-chofer`, datos);
export const apiConfirmarEntrega = (viajeId) => post(`/viajes/${viajeId}/confirmar`, {});
export const apiCancelarViaje = (viajeId, motivo) => post(`/viajes/${viajeId}/cancelar`, { motivo });
export const apiCrearPreferenciaContraEntrega = (viajeId) => post(`/viajes/${viajeId}/crear-preferencia-contra-entrega`, {}).then(d => d);
export const apiReclamarContraEntrega = (viajeId) => post(`/viajes/${viajeId}/reclamar-contra-entrega`, {});
export const apiGuardarCartaPorte = (viajeId, datos) => patch(`/viajes/${viajeId}/carta-porte`, datos);
export const apiGuardarContactoDestino = (viajeId, datos) => patch(`/viajes/${viajeId}/contacto-destino`, datos);
export const apiCalificar = (viajeId, datos) => post(`/viajes/${viajeId}/calificar`, datos);

// ============================================================
// ÓRDENES (CHOFER)
// ============================================================
export const apiGetMisOrdenes = () => get('/ordenes/mis-ordenes').then(d => d.ordenes);
export const apiActualizarStatusOrden = (id, status) => patch(`/ordenes/${id}/status`, { status }).then(d => d.orden);
export const apiReportarIncidencia = (id, datos) => post(`/ordenes/${id}/incidencia`, datos).then(d => d);
export const apiCompletarOrden = (id, datos) => post(`/ordenes/${id}/completar`, datos);
export const apiGetHistorialChofer = () => get('/ordenes/historial').then(d => d.historial);
export const apiActualizarPosicionChofer = (ordenId, lat, lng) => patch(`/ordenes/${ordenId}/posicion`, { lat, lng });
export const apiActualizarPosicionLibre = (lat, lng) => patch(`/ordenes/mi-posicion`, { lat, lng });

// ============================================================
// NOTIFICACIONES
// ============================================================
export const apiGetNotificaciones = () => get('/notificaciones').then(d => d.notificaciones);
export const apiMarcarLeidas = () => patch('/notificaciones/leer', {});
export const apiEliminarNotificacion = (id) => del(`/notificaciones/${id}`);
export const apiEliminarTodasNotificaciones = () => del('/notificaciones');

// ============================================================
// MENSAJES
// ============================================================
export const apiGetMensajes = (viajeId) => get(`/mensajes/${viajeId}`).then(d => d.mensajes);
export const apiGetTicketsSoporte = () => get('/mensajes/admin/tickets').then(d => d.mensajes);
export const apiGetCalificacionesUsuario = (userId) => get(`/perfil/calificaciones/${userId}`).then(d => d.calificaciones);
export const apiGetSoporteProMensajes = () => get('/mensajes/admin/soporte-pro').then(d => d.mensajes);
export const apiEnviarMensaje = (datos) => post('/mensajes', datos).then(d => d.mensaje);

// ============================================================
// RETORNOS
// ============================================================
export const apiGetRetornosDisponibles = () => get('/retornos/disponibles').then(d => d.retornos);
export const apiGetRetornosEmpresa = () => get('/retornos/empresa').then(d => d.retornos);
export const apiPublicarRetorno = (datos) => post('/retornos', datos).then(d => d.retorno);
export const apiOfertarRetorno = (retornoId, datos) => post(`/retornos/${retornoId}/ofertar`, datos).then(d => d.oferta);
export const apiResponderOferta = (ofertaId, datos) => patch(`/retornos/ofertas/${ofertaId}`, datos).then(d => d.oferta);
export const apiClienteResponderContraoferta = (ofertaId, status) => patch(`/retornos/ofertas/${ofertaId}/cliente`, { status }).then(d => d.oferta);
export const apiCancelarOfertaRetorno = (ofertaId) => del(`/retornos/ofertas/${ofertaId}`);
export const apiCrearPreferenciaRetorno = (retornoId, ofertaId) => post(`/retornos/${retornoId}/crear-preferencia`, { ofertaId });
export const apiPagarRetorno = (retornoId, ofertaId, metodoPago) => post(`/retornos/${retornoId}/pagar`, { ofertaId, metodoPago });
export const apiConfirmarEntregaRetorno = (retornoId) => post(`/retornos/${retornoId}/confirmar-entrega`, {});
export const apiCerrarRetorno = (retornoId) => patch(`/retornos/${retornoId}/cerrar`, {});
export const apiCancelarRetorno =(retornoId) => del(`/retornos/${retornoId}`);

// ============================================================
// EMPRESAS / FLOTA / PLANTILLA
// ============================================================
export const apiGetMiEmpresa = () => get('/empresas/mi-empresa').then(d => d.empresa);
export const apiActualizarEmpresa = (datos) => patch('/empresas/mi-empresa', datos).then(d => d.empresa);
export const apiSuscribirPro = () => post('/empresas/suscribir-pro', {}).then(d => d.empresa);
export const apiCrearPreferenciaPro = (periodo = 'mensual') => post('/pagos/crear-preferencia-pro', { periodo }).then(d => d);
export const apiUnirseListaEsperaPro = () => post('/empresas/unirse-lista-espera-pro', {}).then(d => d);
export const apiGetEstadisticasPro = (periodo = 'mes') => get(`/empresas/estadisticas?periodo=${periodo}`).then(d => d);
export const apiCancelarPro = () => post('/empresas/cancelar-pro', {}).then(d => d.empresa);
export const apiGetPlantilla = () => get('/empresas/plantilla').then(d => d.choferes);
export const apiAgregarChofer = (datos) => post('/empresas/plantilla', datos).then(d => d.chofer);
export const apiActualizarChofer = (id, datos) => patch(`/empresas/plantilla/${id}`, datos).then(d => d.chofer);
export const apiEliminarChofer = (id) => del(`/empresas/plantilla/${id}`);
export const apiCrearAccesoChofer = (plantillaId, datos) => post(`/empresas/plantilla/${plantillaId}/acceso`, datos).then(d => d.usuario);
export const apiToggleActivoChofer = (plantillaId, activo) => patch(`/empresas/plantilla/${plantillaId}/activo`, { activo });
export const apiGetFlota = () => get('/empresas/flota').then(d => d.unidades);
export const apiAgregarUnidad = (datos) => post('/empresas/flota', datos).then(d => d.unidad);
export const apiActualizarUnidad = (id, datos) => patch(`/empresas/flota/${id}`, datos).then(d => d.unidad);
export const apiEliminarUnidad = (id) => del(`/empresas/flota/${id}`);

// ── Mantenimiento ──
export const apiGetMantenimiento = () => get('/empresas/mantenimiento').then(d => d.registros);
export const apiAgregarMantenimiento = (datos) => post('/empresas/mantenimiento', datos).then(d => d.registro);
export const apiEliminarMantenimiento = (id) => del(`/empresas/mantenimiento/${id}`);

// ── Diesel ──
export const apiGetDiesel = () => get('/empresas/diesel').then(d => d.registros);
export const apiAgregarDiesel = (datos) => post('/empresas/diesel', datos).then(d => d.registro);
export const apiEliminarDiesel = (id) => del(`/empresas/diesel/${id}`);

// ============================================================
// ADMIN
// ============================================================
export const apiGetEmpresasPendientes = () => get('/admin/empresas/pendientes').then(d => d.empresas);
export const apiGetTodasEmpresas = () => get('/admin/empresas').then(d => d.empresas);
export const apiAdminGetSolicitudes = () => get('/admin/solicitudes').then(d => d.solicitudes);
export const apiAdminGetViajes = () => get('/admin/viajes').then(d => d.viajes);
export const apiAdminGetRetornos = () => get('/admin/retornos').then(d => d.retornos);
export const apiAprobarEmpresa = (id) => post(`/admin/empresas/${id}/aprobar`, {}).then(d => d.empresa);
export const apiRechazarEmpresa = (id, motivo) => post(`/admin/empresas/${id}/rechazar`, { motivo }).then(d => d.empresa);
export const apiSuspenderEmpresa = (id, motivo) => post(`/admin/empresas/${id}/suspender`, { motivo }).then(d => d.empresa);
export const apiAdminVerificarDocumento = (empresaId, docId, status) => patch(`/admin/empresas/${empresaId}/documento/${docId}`, { status }).then(d => d.documentos);
export const apiAdminActualizarComisionEmpresa = (empresaId, comisionPersonalizada) => patch(`/admin/empresas/${empresaId}/comision`, { comisionPersonalizada }).then(d => d.empresa);
export const apiAdminActualizarInsigniasEmpresa = (empresaId, insignias) => patch(`/admin/empresas/${empresaId}/insignias`, { insignias });
export const apiAdminActualizarRfcEmpresa = (empresaId, rfc) => patch(`/admin/empresas/${empresaId}/rfc`, { rfc }).then(d => d.empresa);
export const apiAdminCancelarSolicitud = (id) => del(`/admin/solicitudes/${id}`).then(d => d);
export const apiAdminActualizarInsigniasUsuario = (usuarioId, insignias) => patch(`/admin/usuarios/${usuarioId}/insignias`, { insignias });
export const apiAdminCrearAdmin = (datos) => post('/admin/usuarios', datos);
export const apiAdminActualizarAdmin = (id, cambios) => patch(`/admin/usuarios/${id}`, cambios);
export const apiAdminEliminarAdmin = (id) => del(`/admin/usuarios/${id}`);
export const apiAdminToggleAdmin = (id) => patch(`/admin/usuarios/${id}/toggle`, {});
// TODO: migrar a multipart/form-data para archivos grandes
export const apiTransportistaSubirDocumento = (docId, fileDataUrl, fileName) => patch('/empresas/mi-empresa/documento', { docId, fileDataUrl, fileName }).then(d => d.doc);
export const apiReenviarSolicitud = () => post('/empresas/mi-empresa/reenviar', {});
export const apiAsignarPlanEmpresa = (id, plan) => post(`/admin/empresas/${id}/plan`, { plan }).then(d => d.empresa);
export const apiGetUsuarios = () => get('/admin/usuarios').then(d => d.usuarios);
export const apiGetKpis = () => get('/admin/reportes/kpis').then(d => d.kpis);
export const apiGetReporteViajes = () => get('/admin/reportes/viajes').then(d => d.viajes);
export const apiGetComisiones = () => get('/admin/comisiones').then(d => d.comisiones);
export const apiGetConfig = () => get('/admin/config').then(d => d.config);
export const apiGuardarConfig = (datos) => post('/admin/config', datos).then(d => d.config);
export const apiGetPromos = () => get('/admin/promos').then(d => d.promos);
export const apiCrearPromo = (datos) => post('/admin/promos', datos).then(d => d.promo);
export const apiEditarPromo = (promoId, datos) => patch(`/admin/promos/${promoId}`, datos).then(d => d.promo);
export const apiEliminarPromo = (promoId) => del(`/admin/promos/${promoId}`).then(d => d);
export const apiAsignarPromo = (promoId, empresaId) => post(`/admin/promos/${promoId}/asignar/${empresaId}`).then(d => d);
export const apiQuitarPromo = (promoId, empresaId) => del(`/admin/promos/${promoId}/quitar/${empresaId}`).then(d => d);
export const apiAdminToggleReferidor = (usuarioId, esReferidor) => patch(`/admin/usuarios/${usuarioId}/referidor`, { esReferidor }).then(d => d.usuario);
export const apiAdminCambiarStatusViaje = (viajeId, status, motivo) => patch(`/admin/viajes/${viajeId}/status`, { status, motivo }).then(d => d.viaje);
export const apiAdminAbrirDisputa = (viajeId, motivo) => post(`/admin/viajes/${viajeId}/disputa`, { motivo }).then(d => d.viaje);
export const apiAdminResolverDisputa = (viajeId, resolucion) => post(`/admin/viajes/${viajeId}/resolver-disputa`, { resolucion }).then(d => d.viaje);

// ============================================================
// PERFIL
// ============================================================
export const apiActualizarPerfil = (datos) => patch('/perfil', datos).then(d => d.usuario);
export const apiCambiarPassword = (datos) => patch('/perfil/password', datos);
export const apiMarcarAppInstalada = () => post('/perfil/app-instalada');

// ============================================================
// RETIROS
// ============================================================
// ============================================================
// AUTH — Recuperación de contraseña
// ============================================================
export const apiForgotPassword = (email) => post('/auth/forgot-password', { email });
export const apiResetPassword = (token, password) => post('/auth/reset-password', { token, password });
export const apiVerifyEmail = (token) => get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
export const apiResendVerification = () => post('/auth/resend-verification', {});

export const apiSolicitarRetiro = (datos) => post('/retiros', datos).then(d => d);
export const apiGetMisRetiros = () => get('/retiros/mis-retiros').then(d => d.retiros);
export const apiGetRetiros = () => get('/retiros').then(d => d.retiros);
export const apiGestionarRetiro = (id, accion, notasAdmin) => patch(`/retiros/${id}`, { accion, notasAdmin });
export const apiGetCfdiRetiro = (id, formato = 'pdf') => get(`/retiros/${id}/cfdi?formato=${formato}`);
export const apiRetiroGenerarCfdi = (id) => post(`/retiros/${id}/generar-cfdi`, {});
export const apiRetiroCancelar = (id, notasAdmin = '') => patch(`/retiros/${id}`, { accion: 'cancelar', notasAdmin });

// ============================================================
// PAGOS / ESCROW
// ============================================================
export const apiCrearPreferenciaMercadoPago = (viajeId) => post(`/pagos/crear-preferencia/${viajeId}`).then(d => d);
export const apiGetMisPagos = () => get('/pagos/mis-pagos').then(d => d.pagos);
export const apiGetMisCobros = () => get('/pagos/mis-cobros').then(d => d.pagos);
export const apiGetMisMetricas = () => get('/pagos/mis-metricas').then(d => d.metricas);
export const apiLiberarEscrow = (viajeId) => post(`/pagos/${viajeId}/liberar`).then(d => d);
export const apiGetMetodosPago = () => get('/pagos/metodos').then(d => d.metodos);
export const apiAgregarMetodoPago = (datos) => post('/pagos/metodos', datos).then(d => d.metodo);
export const apiEliminarMetodoPago = (id) => del(`/pagos/metodos/${id}`).then(d => d);
export const apiGetDatosBancarios = () => get('/pagos/datos-bancarios').then(d => d.datosBancarios);
export const apiActualizarDatosBancarios = (datos) => patch('/pagos/datos-bancarios', datos).then(d => d.datosBancarios);
export const apiGetFinanzasAdmin = () => get('/admin/finanzas').then(d => d);
export const apiGetMetricasLive = () => get('/admin/metricas-live').then(d => d.metricas);
export const apiGetActividad = () => get('/admin/actividad').then(d => d.actividad);
export const apiAdminLiberarEscrow = (pagoId) => post(`/admin/finanzas/${pagoId}/liberar`).then(d => d);
export const apiAdminConfirmarPago = (pagoId) => post(`/admin/finanzas/${pagoId}/confirmar-pago`).then(d => d);
export const apiAdminCancelarPago  = (pagoId) => post(`/admin/finanzas/${pagoId}/cancelar`).then(d => d);
export const apiAdminRepararMLM = () => post('/admin/mlm/reparar');
export const apiAdminGestionarComision = (id, accion) => patch(`/admin/comisiones/${id}`, { accion });
export const apiPagarTransportista = (viajeId) => post(`/pagos/${viajeId}/pagar-transportista`).then(d => d);

// ── Facturas ──
export const apiGetMisFacturas = () => get('/facturas/mis-facturas').then(d => d.facturas);
export const apiGetFacturasAdmin = (params = {}) => get('/facturas/admin', params).then(d => d);
export const apiGenerarFactura = (viajeId) => post(`/facturas/generar/${viajeId}`).then(d => d);
export const apiSubirFacturaManual = (viajeId, datos) => post(`/facturas/manual/${viajeId}`, datos).then(d => d);
export const apiCancelarFactura = (id, motivo) => post(`/facturas/${id}/cancelar`, { motivo }).then(d => d);
export const apiActualizarDatosFiscales = (datos) => patch('/facturas/mi-empresa/datos-fiscales', datos).then(d => d);
export const apiFacturamaHealth = () => get('/facturas/health').then(d => d);
export const apiFacturamaDebugRespuesta = () => get('/facturas/debug-respuesta').then(d => d);
export const apiGetFacturaArchivos = (id, force = false) => get(`/facturas/${id}/archivos${force ? '?force=true' : ''}`).then(d => d);
export const apiSolicitarFactura = (viajeId, datos) => post(`/viajes/${viajeId}/solicitar-factura`, datos).then(d => d);
export const apiSubirFacturaTransportista = (viajeId, datos) => post(`/viajes/${viajeId}/factura-subida`, datos).then(d => d);
export const apiFacturaDiagnostico = (cfdiId) => get(`/facturas/diagnostico/${cfdiId}`).then(d => d);
export const apiRecuperarCfdiId = (facturaId, cfdiFacturamaId) => post(`/facturas/${facturaId}/recuperar-cfdi-id`, { cfdiFacturamaId }).then(d => d);

// Descarga segura de documentos privados (CSF, documentos empresa).
// Obtiene una URL firmada de 30 minutos desde el backend.
export const apiGetDownloadUrl = (path) => get(`/storage/download?path=${encodeURIComponent(path)}`).then(d => d.url);

// ── Auditoría ──
export const apiGetAudit = (params = {}) => get('/admin/audit', params).then(d => d);

// ── Tendencias ──
export const apiGetTendencias = () => get('/admin/reportes/tendencias').then(d => d.tendencias);
export const apiGetRetencionesSat = (mes) => get('/admin/retenciones-sat', mes ? { mes } : {}).then(d => d);

// ── Mensajes Admin ──
export const apiEnviarMensajeAdmin = (datos) => post('/admin/mensajes', datos).then(d => d);
export const apiGetMensajesAdmin = () => get('/admin/mensajes').then(d => d.mensajes);
